<template>
  <div class="order-list-container">
    <h1 class="page-title">订单列表</h1>
    <el-card class="content-card">
      <div class="card-header">
        <div class="search-area">
          <el-input
            v-model="searchForm.keyword"
            placeholder="搜索订单ID、用户姓名或手机号"
            style="width: 300px; margin-right: 10px;"
            clearable
            prefix-icon="Search"
          >
          </el-input>
          <el-select 
            v-model="searchForm.status" 
            placeholder="选择状态"
            style="width: 150px; margin-right: 10px;"
          >
            <el-option label="全部状态" value="" />
            <el-option label="待支付" value="pending" />
            <el-option label="已支付" value="paid" />
            <el-option label="已发货" value="shipped" />
            <el-option label="已完成" value="delivered" />
            <el-option label="已取消" value="cancelled" />
            <el-option label="已退款" value="refunded" />
          </el-select>
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            style="width: 300px; margin-right: 10px;"
            clearable
          />
          <el-button type="primary" @click="handleSearch" icon="Search">搜索</el-button>
        </div>
      </div>
      <div class="card-content">
        <el-table v-loading="tableLoading" :data="orders" border stripe style="width: 100%">
          <el-table-column label="订单信息" width="350">
            <template #default="scope">
              <div style="font-weight: bold; margin-bottom: 4px">订单ID: {{ scope.row.id }}</div>
              <div v-for="item in scope.row.items" :key="item.id" class="order-item-product" style="font-size: 12px">
                {{ item.product.name }} x{{ item.quantity }}
              </div>
            </template>
          </el-table-column>
          <el-table-column label="用户信息" width="150">
            <template #default="scope">
              <div>{{ scope.row.user.name }}</div>
              <div style="font-size: 12px; color: #909399">{{ scope.row.user.phone }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="total_amount" label="订单金额" width="120" align="right">
            <template #default="scope">
              <span>¥{{ scope.row.total_amount }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="订单状态" width="120">
            <template #default="scope">
              <el-tag :type="getStatusTagType(scope.row.status)">
                {{ getStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="下单时间" width="180">
            <template #default="scope">
              {{ new Date(scope.row.created_at).toISOString().slice(0, 19).replace('T', ' ') }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button type="primary" size="small" @click="handleViewDetail(scope.row)">
                查看详情
              </el-button>
              <el-button 
                type="info" 
                size="small" 
                @click="handleUpdateStatus(scope.row)"
              >
                更新状态
              </el-button>

            </template>
          </el-table-column>
        </el-table>
        <div class="pagination-container">
          <el-pagination
            :current-page="pagination.currentPage"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>
    
    <!-- 订单详情抽屉 -->
    <el-drawer
      v-model="detailDialogVisible"
      :title="`订单详情 - ${currentOrder?.id || ''}`"
      direction="rtl"
      size="600px"
    >
      <div class="order-detail" v-if="currentOrder">
        <el-card shadow="never" class="detail-card">
          <el-descriptions :column="1" border>
            <el-descriptions-item label="订单ID">{{ currentOrder.id }}</el-descriptions-item>
            <el-descriptions-item label="用户姓名">{{ currentOrder.user.name }}</el-descriptions-item>
            <el-descriptions-item label="用户手机号">{{ currentOrder.user.phone }}</el-descriptions-item>
            <el-descriptions-item label="订单金额">¥{{ currentOrder.total_amount }}</el-descriptions-item>
            <el-descriptions-item label="订单状态">
              <el-tag :type="getStatusTagType(currentOrder.status)">
                {{ getStatusText(currentOrder.status) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="联系人姓名">{{ currentOrder.contact_name }}</el-descriptions-item>
            <el-descriptions-item label="联系人电话">{{ currentOrder.contact_phone }}</el-descriptions-item>
            <el-descriptions-item label="备注">{{ currentOrder.remarks || '无' }}</el-descriptions-item>
            <el-descriptions-item label="创建时间">{{ currentOrder.created_at }}</el-descriptions-item>
            <el-descriptions-item label="更新时间">{{ currentOrder.updated_at }}</el-descriptions-item>
          </el-descriptions>
        </el-card>
        
        <el-card shadow="never" class="detail-card mt-20">
          <template #header>
            <h3 class="detail-section-title m-0">商品信息</h3>
          </template>
          <el-table :data="currentOrder.items" border stripe style="width: 100%" size="small">
            <el-table-column prop="product.name" label="商品名称" />
            <el-table-column prop="quantity" label="数量" width="100" align="center" />
            <el-table-column prop="unit_price" label="单价" width="120" align="right">
              <template #default="scope">
                <span>¥{{ scope.row.unit_price }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="subtotal" label="小计" width="120" align="right">
              <template #default="scope">
                <span>¥{{ scope.row.subtotal }}</span>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </div>
    </el-drawer>
    
    <!-- 更新状态对话框 -->
    <el-dialog
      v-model="statusDialogVisible"
      title="更新订单状态"
      width="500px"
    >
      <el-form :model="statusForm" ref="statusFormRef" label-width="100px">
        <el-form-item label="订单ID" prop="orderId" label-width="80px">
          <el-input v-model="statusForm.orderId" disabled />
        </el-form-item>
        <el-form-item label="当前状态" prop="currentStatus" label-width="80px">
          <el-tag :type="getStatusTagType(statusForm.currentStatus)">
            {{ getStatusText(statusForm.currentStatus) }}
          </el-tag>
        </el-form-item>
        <el-form-item label="新状态" prop="newStatus" required>
          <el-select v-model="statusForm.newStatus" placeholder="请选择新状态" style="width: 100%">
            <el-option label="待支付" value="pending" />
            <el-option label="已支付" value="paid" />
            <el-option label="已发货" value="shipped" />
            <el-option label="已完成" value="delivered" />
            <el-option label="已取消" value="cancelled" />
            <el-option label="已退款" value="refunded" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="statusDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmitStatus">确定</el-button>
        </span>
      </template>
    </el-dialog>
    

  </div>
</template>

<script setup>
import { ref, onMounted, reactive, watch } from 'vue';
import { ElMessage } from 'element-plus';
import request from '../../api/axios';

// 订单列表数据
const orders = ref([]);
// 表格加载状态
const tableLoading = ref(false);
// 搜索表单数据
const searchForm = reactive({
  keyword: '',
  status: '',
  start_date: '',
  end_date: ''
});
// 日期范围
const dateRange = ref([]);
// 分页数据
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
});
// 订单详情对话框
const detailDialogVisible = ref(false);
const currentOrder = ref(null);
// 更新状态对话框
const statusDialogVisible = ref(false);
const statusFormRef = ref();
const statusForm = reactive({
  orderId: '',
  currentStatus: '',
  newStatus: ''
});


// 监听日期范围变化
watch(
  () => dateRange.value,
  (newVal) => {
    if (newVal && newVal.length === 2) {
      searchForm.start_date = newVal[0];
      searchForm.end_date = newVal[1];
    } else {
      searchForm.start_date = '';
      searchForm.end_date = '';
    }
  }
);

// 获取订单列表
const fetchOrders = async () => {
  try {
    tableLoading.value = true;
    const response = await request({
      url: '/manage/orders',
      method: 'get',
      params: {
        keyword: searchForm.keyword,
        status: searchForm.status,
        start_date: searchForm.start_date,
        end_date: searchForm.end_date,
        page: pagination.currentPage,
        pageSize: pagination.pageSize
      }
    });
    orders.value = response.data?.data?.data || [];
    pagination.total = response.data?.data?.total || 0;
  } catch (error) {
    ElMessage.error('获取订单列表失败');
    console.error('获取订单列表失败:', error);
  } finally {
    tableLoading.value = false;
  }
};

// 页面加载时获取数据
onMounted(() => {
  fetchOrders();
});

// 搜索订单
// eslint-disable-next-line no-unused-vars
const handleSearch = () => {
  pagination.currentPage = 1;
  fetchOrders();
};

// 分页大小变化
// eslint-disable-next-line no-unused-vars
const handleSizeChange = (size) => {
  pagination.pageSize = size;
  fetchOrders();
};

// 当前页码变化
// eslint-disable-next-line no-unused-vars
const handleCurrentChange = (current) => {
  pagination.currentPage = current;
  fetchOrders();
};

// 查看详情
// eslint-disable-next-line no-unused-vars
const handleViewDetail = (row) => {
  currentOrder.value = row;
  detailDialogVisible.value = true;
};

// 更新状态
// eslint-disable-next-line no-unused-vars
const handleUpdateStatus = (row) => {
  statusForm.orderId = row.id;
  statusForm.currentStatus = row.status;
  statusForm.newStatus = '';
  statusDialogVisible.value = true;
};

// 提交状态更新
// eslint-disable-next-line no-unused-vars
const handleSubmitStatus = async () => {
  if (!statusFormRef.value) return;
  
  try {
    await statusFormRef.value.validate();
    
    await request({
      url: `/manage/orders/${statusForm.orderId}/update-status`,
      method: 'put',
      data: {
        status: statusForm.newStatus
      }
    });
    
    ElMessage.success('状态更新成功');
    statusDialogVisible.value = false;
    fetchOrders();
  } catch (error) {
    if (error.name === 'Error') {
      // 表单验证失败，不显示错误信息
      return;
    }
    ElMessage.error('状态更新失败');
    console.error('状态更新失败:', error);
  }
};



// 获取状态标签类型
// eslint-disable-next-line no-unused-vars
const getStatusTagType = (status) => {
  const statusMap = {
    'pending': 'warning',
    'paid': 'primary',
    'shipped': 'info',
    'delivered': 'success',
    'cancelled': 'danger',
    'refunded': 'danger'
  };
  return statusMap[status] || 'info';
};

// 获取状态文本
// eslint-disable-next-line no-unused-vars
const getStatusText = (status) => {
  const statusMap = {
    'pending': '待支付',
    'paid': '已支付',
    'shipped': '已发货',
    'delivered': '已完成',
    'cancelled': '已取消',
    'refunded': '已退款'
  };
  return statusMap[status] || status;
};


</script>

<style scoped>
.order-list-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.search-area {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.card-content {
  padding: 20px 0;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.order-item-product {
  margin-bottom: 4px;
}

.detail-section-title {
  margin: 20px 0 10px;
  font-size: 16px;
  font-weight: bold;
}

/* 订单详情抽屉样式 */
.order-detail {
  padding: 0;
}

.detail-card {
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
}

.mt-20 {
  margin-top: 20px;
}

.el-descriptions {
  margin-bottom: 0;
}

.el-descriptions__label {
  font-weight: bold;
  color: #606266;
}

.el-descriptions__value {
  color: #303133;
}

.el-drawer__header {
  border-bottom: 1px solid #ebeef5;
  padding: 16px 20px;
  margin-bottom: 20px;
}

.el-drawer__title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.el-drawer__body {
  padding: 0 20px 20px;
}

.el-card__header {
  padding: 12px 20px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
}
</style>